import {
  FormControl,
  Input,
  Textarea,
  Stack,
  Box,
  FormErrorMessage,
  Flex,
  IconButton,
  Text,
  Button,
  useToast,
  HStack,
  Checkbox,
  CheckboxGroup,
  Spinner,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  RiAddLine,
  RiSparklingLine,
  RiMoneyDollarCircleLine,
  RiBarChartBoxLine,
  RiPieChartLine,
  RiArrowDownSLine,
  RiArrowLeftLine,
  RiCloseLine,
  RiCheckLine,
} from 'react-icons/ri';
import {
  GetSurveysDocument,
  SurveyGroup,
  SurveyQuestionType,
  SurveyType,
  useCreateAbTestMutation,
  useCreateSurveyWithQuestionsMutation,
  useGetSurveyGroupNamesQuery,
  useGetSurveysQuery,
} from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';
import { SurveyGenerationModal } from './SurveyGenerationModal';
import { HelivanSelector } from '../helivan/HelivanSelector';

interface QuestionOption {
  id: string;
  value: string;
}

interface SurveyQuestion {
  id: string;
  title: string;
  type: SurveyQuestionType;
  required: boolean;
  order: number;
  options: QuestionOption[];
  aiContext?: string;
}

export const SurveyCreateNew = () => {
  const [searchParams] = useSearchParams();
  const defaultGroupId = searchParams.get('defaultGroupId');
  const [createABTest, { loading: createABTestLoading }] =
    useCreateAbTestMutation({
      refetchQueries: [{ query: GetSurveysDocument }],
    });
  const navigate = useNavigate();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [selectedSurveyGroupId, setSelectedSurveyGroupId] = useState<
    string | null
  >(null);
  const [description, setDescription] = useState('');
  const [descriptionPrivate, setDescriptionPrivate] = useState('');
  const [bounty, setBounty] = useState<number | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [messageA, setMessageA] = useState('');
  const [messageB, setMessageB] = useState('');
  const [messageAContext, setMessageAContext] = useState('');
  const [messageBContext, setMessageBContext] = useState('');
  const [baseQuestion, setBaseQuestion] = useState('Do you agree with this?');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [viewType, setViewType] = useState<'survey' | 'abTesting'>('survey');
  const {
    data: surveyGroupsData,
    loading: surveyGroupsLoading,
    error: surveyGroupsError,
  } = useGetSurveyGroupNamesQuery();
  const surveyGroups = surveyGroupsData?.getSurveyGroupsBasicData ?? [];

  // Debug logging
  useEffect(() => {
    if (surveyGroupsError) {
      console.error('Error loading survey groups:', surveyGroupsError);
    }
  }, [surveyGroupsData, surveyGroupsLoading, surveyGroupsError, surveyGroups]);

  // If a defaultGroupId is provided via query param, preselect it once groups load
  useEffect(() => {
    if (!selectedSurveyGroupId && defaultGroupId && surveyGroups.length > 0) {
      const exists = surveyGroups.some((g) => g.id === defaultGroupId);
      if (exists) {
        setSelectedSurveyGroupId(defaultGroupId);
      }
    }
  }, [defaultGroupId, surveyGroups, selectedSurveyGroupId]);
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [selectedDemographics, setSelectedDemographics] = useState<{
    ageRanges: string[];
    incomes: string[];
    ethnicities: string[];
    genders: string[];
    hasChildren: string[];
  }>({
    ageRanges: [],
    incomes: [],
    ethnicities: [],
    genders: [],
    hasChildren: [],
  });

  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [createSurvey, { loading }] = useCreateSurveyWithQuestionsMutation({
    refetchQueries: [{ query: GetSurveysDocument }],
  });

  const addQuestion = () => {
    const newQuestion: SurveyQuestion = {
      id: `question-${Date.now()}`,
      title: '',
      type: SurveyQuestionType.Text,
      required: true,
      order: questions.length,
      options: [],
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const updateQuestion = (
    id: string,
    field: keyof SurveyQuestion,
    value: any
  ) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === id) {
          // If changing question type, reset options for non-choice types
          if (field === 'type') {
            const newType = value as SurveyQuestionType;
            if (
              newType !== SurveyQuestionType.MultipleChoice &&
              newType !== SurveyQuestionType.MultiSelect
            ) {
              return { ...q, [field]: value, options: [] };
            }
          }
          return { ...q, [field]: value };
        }
        return q;
      })
    );
  };

  const addOption = (questionId: string) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: [
              ...q.options,
              {
                id: `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                value: '',
              },
            ],
          };
        }
        return q;
      })
    );
  };

  const updateOption = (
    questionId: string,
    optionId: string,
    value: string
  ) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map((opt) => {
              if (opt.id === optionId) {
                return { ...opt, value };
              }
              return opt;
            }),
          };
        }
        return q;
      })
    );
  };

  const removeOption = (questionId: string, optionId: string) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.filter((opt) => opt.id !== optionId),
          };
        }
        return q;
      })
    );
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (questions.length === 0) {
      newErrors.questions = 'At least one question is required';
    } else {
      questions.forEach((q, index) => {
        if (!q.title.trim()) {
          newErrors[`question-${index}-title`] = 'Question title is required';
        }

        if (
          (q.type === SurveyQuestionType.MultipleChoice ||
            q.type === SurveyQuestionType.MultiSelect) &&
          q.options.length < 2
        ) {
          newErrors[`question-${index}-options`] =
            'At least two options are required';
        }

        q.options.forEach((opt, optIndex) => {
          if (!opt.value.trim()) {
            newErrors[`question-${index}-option-${optIndex}`] =
              'Option cannot be empty';
          }
        });
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isSurveyValid = (): boolean => {
    if (!title.trim()) return false;
    if (questions.length === 0) return false;
    for (const q of questions) {
      if (!q.title.trim()) return false;
      if (
        q.type === SurveyQuestionType.MultipleChoice ||
        q.type === SurveyQuestionType.MultiSelect
      ) {
        if (q.options.length < 2) return false;
        for (const opt of q.options) {
          if (!opt.value.trim()) return false;
        }
      }
    }
    return true;
  };

  // Demographics data
  const ageRanges = [
    'Under 18',
    '18-24',
    '25-34',
    '35-44',
    '45-54',
    '55-64',
    '65+',
  ];

  const usStates = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ];

  const incomes = [
    'Under $25,000',
    '$25,000 - $49,999',
    '$50,000 - $74,999',
    '$75,000 - $99,999',
    '$100,000 - $149,999',
    '$150,000 - $199,999',
    '$200,000+',
  ];

  const ethnicities = [
    'White',
    'Black or African American',
    'Hispanic or Latino',
    'Asian',
    'Native American',
    'Pacific Islander',
    'Other',
  ];

  const genders = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];

  const hasChildrenOptions = ['Yes', 'No'];

  const handleSubmit = async () => {
    if (!selectedSurveyGroupId) {
      toast({
        title: 'Please select a survey group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (viewType === 'abTesting') {
      await createABTest({
        variables: {
          input: {
            title,
            baseQuestion,
            messageA,
            messageB,
            surveyGroupId: selectedSurveyGroupId,
            messageAContext,
            messageBContext,
          },
        },
        onCompleted: () => {
          navigate('/surveys');
        },
      });
    } else if (viewType === 'survey') {
      if (!validateForm()) {
        return;
      }

      await createSurvey({
        variables: {
          input: {
            title,
            surveyGroupId: selectedSurveyGroupId,
            description: description || undefined,
            type:
              viewType === 'survey'
                ? SurveyType.Questionnaire
                : SurveyType.AbTesting,
            bountyInCents: bounty ? bounty * 100 : undefined,
            questions: questions.map((q) => ({
              title: q.title,
              aiContext: q.aiContext || undefined,
              type: q.type,
              required: q.required,
              order: q.order,
              options:
                q.type === SurveyQuestionType.MultipleChoice ||
                q.type === SurveyQuestionType.MultiSelect
                  ? q.options.map((opt) => opt.value)
                  : undefined,
            })),
          },
        },
        onCompleted: () => {
          navigate('/surveys');
        },
      });
    }
  };

  const handleGenerationOptionSelected = async (
    option: 'generated' | 'partial' | 'human'
  ) => {
    if (!selectedSurveyGroupId) {
      toast({
        title: 'Please select a survey group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await createSurvey({
        variables: {
          input: {
            title,
            surveyGroupId: selectedSurveyGroupId,
            description: description || undefined,
            type:
              viewType === 'survey'
                ? SurveyType.Questionnaire
                : SurveyType.AbTesting,
            bountyInCents: bounty ? bounty * 100 : undefined,
            questions: questions.map((q) => ({
              title: q.title,
              type: q.type,
              required: q.required,
              order: q.order,
              aiContext: q.aiContext || undefined,
              options:
                q.type === SurveyQuestionType.MultipleChoice ||
                q.type === SurveyQuestionType.MultiSelect
                  ? q.options.map((opt) => opt.value)
                  : undefined,
            })),
          },
        },
      });

      if (result.data?.createSurveyWithQuestions) {
        // Navigate to surveys page after successful creation
        navigate('/surveys');
      }
    } catch (error) {
      console.error('Error creating survey:', error);
      toast({
        title: 'Failed to create survey',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    navigate('/surveys');
  };

  // Automatically add a default question when component mounts
  useEffect(() => {
    if (questions.length === 0) {
      addQuestion();
    }
  }, []);

  // const getQuestionTypeIcon = (type: SurveyQuestionType) => {
  //   switch (type) {
  //     case SurveyQuestionType.MultipleChoice:
  //       return <RiPieChartLine />;
  //     case SurveyQuestionType.MultiSelect:
  //       return <RiBarChartBoxLine />;
  //     default:
  //       return null;
  //   }
  // };

  const renderDemographicFilter = (filterType: string) => {
    const getDemographicOptions = () => {
      switch (filterType) {
        case 'ageRanges':
          return ageRanges;
        case 'incomes':
          return incomes;
        case 'ethnicities':
          return ethnicities;
        case 'genders':
          return genders;
        case 'hasChildren':
          return hasChildrenOptions;
        default:
          return [];
      }
    };

    const getFilterLabel = () => {
      switch (filterType) {
        case 'ageRanges':
          return 'Age Range';
        case 'incomes':
          return 'Income';
        case 'ethnicities':
          return 'Ethnicity';
        case 'genders':
          return 'Gender';
        case 'hasChildren':
          return 'Has Children';
        default:
          return '';
      }
    };

    return (
      <Box>
        <HelivanText variant="largeBody" fontWeightOverride={600} mb={2}>
          {getFilterLabel()}
        </HelivanText>
        <CheckboxGroup
          value={
            selectedDemographics[
              filterType as keyof typeof selectedDemographics
            ]
          }
          onChange={(values) => {
            setSelectedDemographics({
              ...selectedDemographics,
              [filterType]: values as string[],
            });
          }}
        >
          <Stack spacing={2}>
            {getDemographicOptions().map((option) => (
              <Checkbox
                key={option}
                value={option}
                colorScheme="blue"
                borderColor={HelivanColor.border.primary}
              >
                <HelivanText variant="largeBody">{option}</HelivanText>
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      </Box>
    );
  };

  const renderQuestionsForm = () => (
    <Flex width="full" flexDirection="column" gap={10} p={10}>
      <Flex width="full" justifyContent="space-between" alignItems="center">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            viewType === 'survey' ? 'Survey Title' : 'A/B Test Title'
          }
          width="300px"
          borderRadius="0px"
          borderWidth="0px"
          borderBottomWidth="1px"
          borderColor={HelivanColor.border.primary}
          focusBorderColor="transparent"
          _hover={{
            borderColor: 'transparent',
            borderBottomColor: 'black',
          }}
          _focus={{
            borderColor: 'transparent',
            borderBottomColor: 'black',
            boxShadow: 'none',
          }}
          _focusVisible={{
            borderColor: 'transparent',
            borderBottomColor: 'black',
            boxShadow: 'none',
          }}
        />
        <HStack spacing={4}>
          <HelivanButton
            variant="ghost"
            onClick={() => {
              setViewType('survey');
            }}
            textDecoration={viewType === 'survey' ? 'underline' : undefined}
          >
            Survey
          </HelivanButton>
          <HelivanButton
            variant="ghost"
            onClick={() => {
              setViewType('abTesting');
            }}
            textDecoration={viewType === 'abTesting' ? 'underline' : undefined}
          >
            A/B Testing
          </HelivanButton>
        </HStack>
      </Flex>
      {viewType === 'survey' ? (
        <Flex width="full" justifyContent="center">
          <Stack width="full" spacing={6} maxWidth="500px">
            {/* Basic Information */}
            <Box>
              <Stack spacing={4}>
                <FormControl>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short description (for your internal documentation)"
                    background="#F7F8F9"
                    borderRadius="8px"
                    borderColor={HelivanColor.border.primary}
                    borderWidth="1px"
                    _focus={{
                      borderColor: 'black',
                    }}
                  />
                </FormControl>
              </Stack>
            </Box>

            <Stack spacing={2}>
              <HelivanText variant="largeBody+">
                Select Survey Group
              </HelivanText>
              {surveyGroupsLoading ? (
                <Spinner />
              ) : surveyGroupsError ? (
                <Box>
                  <Text color="red.500" fontSize="sm">
                    Error loading survey groups: {surveyGroupsError.message}
                  </Text>
                </Box>
              ) : surveyGroups.length === 0 ? (
                <Box>
                  <Text color="orange.500" fontSize="sm">
                    No survey groups found. You may need to create one first.
                  </Text>
                </Box>
              ) : (
                <HelivanSelector
                  type="secondary"
                  defaultValue={defaultGroupId ?? undefined}
                  options={
                    surveyGroupsData?.getSurveyGroupsBasicData
                      ? surveyGroupsData?.getSurveyGroupsBasicData?.map(
                          (surveyGroup) => ({
                            key: surveyGroup.id,
                            value: surveyGroup.name,
                          })
                        )
                      : []
                  }
                  selectedOption={selectedSurveyGroupId}
                  onSelect={(value) => {
                    setSelectedSurveyGroupId(value as string | null);
                  }}
                />
              )}
            </Stack>

            {/* Audience Filters */}
            {/* <Box>
            <Flex alignItems="center" mb={4}>
              <Switch
                isChecked={useFilters}
                onChange={(e) => setUseFilters(e.target.checked)}
                colorScheme="blue"
                mr={3}
              />
              <HelivanText variant="largeBody+">
                Target Specific Audience
              </HelivanText>
            </Flex>

            {useFilters && (
              <Stack spacing={4}>
                <Box>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      rightIcon={<RiArrowDownSLine />}
                      variant="outline"
                      borderColor={HelivanColor.border.primary}
                      _hover={{
                        borderColor: HelivanColor.border.hover,
                      }}
                      _active={{
                        borderColor: HelivanColor.border.primary,
                      }}
                      background="#F7F8F9"
                    >
                      Add Filters{' '}
                      {activeFilters.length > 0 && `(${activeFilters.length})`}
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        onClick={() => {
                          setActiveFilters((prev) =>
                            prev.includes('ageRanges')
                              ? prev.filter((f) => f !== 'ageRanges')
                              : [...prev, 'ageRanges']
                          );
                        }}
                      >
                        <HStack spacing={2} width="full">
                          <Text>Age Range</Text>
                          {activeFilters.includes('ageRanges') && (
                            <RiCheckLine />
                          )}
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setActiveFilters((prev) =>
                            prev.includes('incomes')
                              ? prev.filter((f) => f !== 'incomes')
                              : [...prev, 'incomes']
                          );
                        }}
                      >
                        <HStack spacing={2} width="full">
                          <Text>Income</Text>
                          {activeFilters.includes('incomes') && <RiCheckLine />}
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setActiveFilters((prev) =>
                            prev.includes('ethnicities')
                              ? prev.filter((f) => f !== 'ethnicities')
                              : [...prev, 'ethnicities']
                          );
                        }}
                      >
                        <HStack spacing={2} width="full">
                          <Text>Ethnicity</Text>
                          {activeFilters.includes('ethnicities') && (
                            <RiCheckLine />
                          )}
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setActiveFilters((prev) =>
                            prev.includes('genders')
                              ? prev.filter((f) => f !== 'genders')
                              : [...prev, 'genders']
                          );
                        }}
                      >
                        <HStack spacing={2} width="full">
                          <Text>Gender</Text>
                          {activeFilters.includes('genders') && <RiCheckLine />}
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setActiveFilters((prev) =>
                            prev.includes('hasChildren')
                              ? prev.filter((f) => f !== 'hasChildren')
                              : [...prev, 'hasChildren']
                          );
                        }}
                      >
                        <HStack spacing={2} width="full">
                          <Text>Has Children</Text>
                          {activeFilters.includes('hasChildren') && (
                            <RiCheckLine />
                          )}
                        </HStack>
                      </MenuItem>
                    </MenuList>
                  </Menu>

                  {activeFilters.length > 0 && (
                    <Stack spacing={4} mt={4}>
                      {activeFilters.map((filterType) => (
                        <Box
                          key={filterType}
                          p={4}
                          bg={HelivanColor.background.secondary}
                          borderRadius="md"
                        >
                          {renderDemographicFilter(filterType)}
                        </Box>
                      ))}
                    </Stack>
                  )}
                    */}

            {/* Display selected filters */}
            {/* <Wrap mt={4}>
                    {Object.entries(selectedDemographics).map(([key, values]) =>
                      values.map((value) => (
                        <WrapItem key={`${key}-${value}`}>
                          <Tag
                            size="md"
                            borderRadius="full"
                            variant="solid"
                            colorScheme="blue"
                          >
                            <TagLabel>{value}</TagLabel>
                            <TagCloseButton
                              onClick={() => {
                                setSelectedDemographics({
                                  ...selectedDemographics,
                                  [key]: (
                                    selectedDemographics[
                                      key as keyof typeof selectedDemographics
                                    ] as string[]
                                  ).filter((v) => v !== value),
                                });
                              }}
                            />
                          </Tag>
                        </WrapItem>
                      ))
                    )}
                  </Wrap> */}
            {/* </Box>
              </Stack>
            )}
          </Box>  */}

            {/* Questions */}
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <HelivanText variant="xlargeBody+">Questions</HelivanText>
              </Flex>

              {errors.questions && (
                <Text color="red.500" fontSize="sm" mb={4}>
                  {errors.questions}
                </Text>
              )}

              <Stack spacing={4}>
                {questions.map((question, index) => (
                  <Box
                    key={question.id}
                    p={4}
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    borderRadius="md"
                    bg="white"
                    background="#F7F8F9"
                  >
                    <Stack spacing={3}>
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                      >
                        <FormControl
                          isInvalid={!!errors[`question-${index}-title`]}
                        >
                          <Input
                            value={question.title}
                            onChange={(e) =>
                              updateQuestion(
                                question.id,
                                'title',
                                e.target.value
                              )
                            }
                            placeholder={'Untitled question'}
                            borderRadius="0px"
                            borderWidth="0px"
                            borderBottomWidth="1px"
                            borderColor={HelivanColor.border.primary}
                            bg="#F7F8F9"
                            focusBorderColor="transparent"
                            _hover={{
                              borderColor: 'transparent',
                              borderBottomColor: 'black',
                            }}
                            _focus={{
                              borderColor: 'transparent',
                              borderBottomColor: 'black',
                              boxShadow: 'none',
                            }}
                            _focusVisible={{
                              borderColor: 'transparent',
                              borderBottomColor: 'black',
                              boxShadow: 'none',
                            }}
                          />
                          <FormErrorMessage>
                            {errors[`question-${index}-title`]}
                          </FormErrorMessage>
                        </FormControl>
                        <IconButton
                          aria-label="Remove question"
                          icon={<RiCloseLine />}
                          size="sm"
                          variant="ghost"
                          onClick={() => removeQuestion(question.id)}
                        />
                      </Flex>

                      <Flex gap={4}>
                        <HelivanSelector
                          type="secondary"
                          options={[
                            // { key: SurveyQuestionType.Text, value: 'Text' },
                            // {
                            //   key: SurveyQuestionType.TextNumeric,
                            //   value: 'Numeric',
                            // },
                            {
                              key: SurveyQuestionType.MultipleChoice,
                              value: 'Multiple Choice',
                            },
                            // {
                            //   key: SurveyQuestionType.MultiSelect,
                            //   value: 'Multi-Select',
                            // },
                          ]}
                          selectedOption={question.type}
                          onSelect={(value) =>
                            updateQuestion(
                              question.id,
                              'type',
                              value as SurveyQuestionType
                            )
                          }
                        />
                      </Flex>

                      {(question.type === SurveyQuestionType.MultipleChoice ||
                        question.type === SurveyQuestionType.MultiSelect) && (
                        <Box>
                          <HelivanText
                            variant="smallBody+"
                            fontWeightOverride={500}
                            mb={2}
                          >
                            Options
                          </HelivanText>
                          {errors[`question-${index}-options`] && (
                            <Text color="red.500" fontSize="sm" mb={2}>
                              {errors[`question-${index}-options`]}
                            </Text>
                          )}
                          <Stack spacing={2}>
                            {question.options.map((option, optIndex) => (
                              <Flex key={option.id} gap={2}>
                                <FormControl
                                  isInvalid={
                                    !!errors[
                                      `question-${index}-option-${optIndex}`
                                    ]
                                  }
                                >
                                  <Input
                                    value={option.value}
                                    onChange={(e) =>
                                      updateOption(
                                        question.id,
                                        option.id,
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Option ${optIndex + 1}`}
                                    size="sm"
                                    borderRadius="0px"
                                    borderWidth="0px"
                                    borderBottomWidth="1px"
                                    borderColor={HelivanColor.border.primary}
                                    focusBorderColor="transparent"
                                    _hover={{
                                      borderColor: 'transparent',
                                      borderBottomColor: 'black',
                                    }}
                                    _focus={{
                                      borderColor: 'transparent',
                                      borderBottomColor: 'black',
                                      boxShadow: 'none',
                                    }}
                                    _focusVisible={{
                                      borderColor: 'transparent',
                                      borderBottomColor: 'black',
                                      boxShadow: 'none',
                                    }}
                                  />
                                  <FormErrorMessage>
                                    {
                                      errors[
                                        `question-${index}-option-${optIndex}`
                                      ]
                                    }
                                  </FormErrorMessage>
                                </FormControl>
                                <IconButton
                                  aria-label="Remove option"
                                  icon={<RiCloseLine />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    removeOption(question.id, option.id)
                                  }
                                />
                              </Flex>
                            ))}
                            <Flex
                              justifyContent="space-between"
                              width="full"
                              alignItems="center"
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                width="fit-content"
                                alignSelf="flex-start"
                                color={HelivanColor.text.blueMid}
                                onClick={() => addOption(question.id)}
                                leftIcon={<RiAddLine />}
                                borderColor={HelivanColor.border.primary}
                                _hover={{
                                  borderColor: HelivanColor.border.hover,
                                  bg: HelivanColor.background.secondary,
                                }}
                              >
                                Add Option
                              </Button>
                              <Checkbox
                                isChecked={question.required}
                                onChange={(e) =>
                                  updateQuestion(
                                    question.id,
                                    'required',
                                    e.target.checked
                                  )
                                }
                                colorScheme="blackAlpha"
                              >
                                <HelivanText variant="smallBody+">
                                  Required
                                </HelivanText>
                              </Checkbox>
                            </Flex>
                          </Stack>
                        </Box>
                      )}
                      <Stack spacing={2}>
                        <HelivanText
                          variant="smallBody+"
                          fontWeightOverride={500}
                        >
                          Context (recommended if your question requires domain
                          knowledge)
                        </HelivanText>
                        <Textarea
                          value={question.aiContext}
                          onChange={(e) =>
                            updateQuestion(
                              question.id,
                              'aiContext',
                              e.target.value
                            )
                          }
                          placeholder="e.g. 'The Avengers are a team of superheroes'"
                          background="#F7F8F9"
                          borderRadius="8px"
                          borderColor={HelivanColor.border.primary}
                          borderWidth="1px"
                          _focus={{
                            borderColor: 'black',
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                ))}
                <HelivanButton
                  size="sm"
                  width="fit-content"
                  alignSelf="flex-end"
                  variant="outline"
                  onClick={addQuestion}
                  leftIcon={<RiAddLine />}
                >
                  Add Question
                </HelivanButton>
              </Stack>
            </Box>
            <HelivanButton
              variant="primary"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Creating..."
              alignSelf="center"
              mb={10}
              isDisabled={loading || !isSurveyValid()}
            >
              <HStack spacing={1}>
                <RiSparklingLine color="white" size={16} />
                <HelivanText colorOverride="white" variant="largeBody+">
                  Create Survey
                </HelivanText>
              </HStack>
            </HelivanButton>
          </Stack>
        </Flex>
      ) : (
        <Flex width="full" justifyContent="center">
          <Stack width="full" spacing={6} maxWidth="500px">
            {/* Base Question */}
            <Box>
              <Stack spacing={2}>
                <HelivanText variant="largeBody+">
                  Comparison Question
                </HelivanText>

                <Input
                  value={baseQuestion}
                  onChange={(e) => setBaseQuestion(e.target.value)}
                  placeholder="Question to ask about the messages"
                  background="#F7F8F9"
                  borderRadius="8px"
                  borderColor={HelivanColor.border.primary}
                  borderWidth="1px"
                  _focus={{
                    borderColor: 'black',
                  }}
                />
                <HelivanText variant="smallBody">
                  Note: This should be a yes/no question
                </HelivanText>
              </Stack>
            </Box>

            {/* Message A */}
            <Box>
              <Stack spacing={2}>
                <HelivanText variant="largeBody+">Message A</HelivanText>
                <Textarea
                  value={messageA}
                  onChange={(e) => setMessageA(e.target.value)}
                  placeholder="Enter your first message variant..."
                  background="#F7F8F9"
                  borderRadius="8px"
                  borderColor={HelivanColor.border.primary}
                  borderWidth="1px"
                  minHeight="150px"
                  _focus={{
                    borderColor: 'black',
                  }}
                />
                <HelivanText variant="smallBody+" fontWeightOverride={500}>
                  Context (optional)
                </HelivanText>
                <Textarea
                  value={messageAContext}
                  onChange={(e) => setMessageAContext(e.target.value)}
                  placeholder="Add context for Message A..."
                  background="#F7F8F9"
                  borderRadius="8px"
                  borderColor={HelivanColor.border.primary}
                  borderWidth="1px"
                  minHeight="80px"
                  _focus={{
                    borderColor: 'black',
                  }}
                />
              </Stack>
            </Box>

            {/* Message B */}
            <Box>
              <Stack spacing={2}>
                <HelivanText variant="largeBody+">Message B</HelivanText>
                <Textarea
                  value={messageB}
                  onChange={(e) => setMessageB(e.target.value)}
                  placeholder="Enter your second message variant..."
                  background="#F7F8F9"
                  borderRadius="8px"
                  borderColor={HelivanColor.border.primary}
                  borderWidth="1px"
                  minHeight="150px"
                  _focus={{
                    borderColor: 'black',
                  }}
                />
                <HelivanText variant="smallBody+" fontWeightOverride={500}>
                  Context (optional)
                </HelivanText>
                <Textarea
                  value={messageBContext}
                  onChange={(e) => setMessageBContext(e.target.value)}
                  placeholder="Add context for Message B..."
                  background="#F7F8F9"
                  borderRadius="8px"
                  borderColor={HelivanColor.border.primary}
                  borderWidth="1px"
                  minHeight="80px"
                  _focus={{
                    borderColor: 'black',
                  }}
                />
              </Stack>
            </Box>

            {/* Survey Group Selection */}
            <Stack spacing={2}>
              <HelivanText variant="largeBody+">
                Select Survey Group
              </HelivanText>
              {surveyGroupsLoading ? (
                <Spinner />
              ) : surveyGroupsError ? (
                <Box>
                  <Text color="red.500" fontSize="sm">
                    Error loading survey groups: {surveyGroupsError.message}
                  </Text>
                </Box>
              ) : surveyGroups.length === 0 ? (
                <Box>
                  <Text color="orange.500" fontSize="sm">
                    No survey groups found. You may need to create one first.
                  </Text>
                </Box>
              ) : (
                <HelivanSelector
                  type="secondary"
                  defaultValue={defaultGroupId ?? undefined}
                  options={
                    surveyGroupsData?.getSurveyGroupsBasicData
                      ? surveyGroupsData?.getSurveyGroupsBasicData?.map(
                          (surveyGroup) => ({
                            key: surveyGroup.id,
                            value: surveyGroup.name,
                          })
                        )
                      : []
                  }
                  selectedOption={selectedSurveyGroupId}
                  onSelect={(value) => {
                    setSelectedSurveyGroupId(value as string | null);
                  }}
                />
              )}
            </Stack>

            {/* Run Test Button */}
            <HelivanButton
              variant="primary"
              onClick={() => {
                handleSubmit();
              }}
              isLoading={createABTestLoading}
              alignSelf="center"
              mb={10}
              isDisabled={
                !title || !messageA || !messageB || !selectedSurveyGroupId
              }
            >
              <HStack spacing={1}>
                <RiSparklingLine color="white" size={16} />
                <HelivanText colorOverride="white" variant="largeBody+">
                  Run Test
                </HelivanText>
              </HStack>
            </HelivanButton>
          </Stack>
        </Flex>
      )}
    </Flex>
  );

  return (
    <>
      <Stack
        width="full"
        height="full"
        spacing={0}
        bg={HelivanColor.background.primary}
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="8px"
        overflow="hidden"
      >
        {/* Header */}
        <Flex
          width="full"
          px="30px"
          py="16px"
          borderBottomColor={HelivanColor.border.primary}
          bg="white"
          borderTopRadius="12px"
          borderBottomWidth="1px"
          justifyContent="space-between"
          alignItems="center"
        >
          <HStack spacing={4}>
            <IconButton
              aria-label="Go back"
              icon={<RiArrowLeftLine />}
              variant="ghost"
              onClick={handleCancel}
            />
            <HelivanText variant="largeSubheader">New Survey</HelivanText>
          </HStack>
        </Flex>

        {/* Content */}
        <Flex overflowY="auto" justifyContent="center" width="full">
          {renderQuestionsForm()}
        </Flex>
      </Stack>

      {/* Survey Generation Modal */}
      <SurveyGenerationModal
        isOpen={isGenerationModalOpen}
        onClose={() => setIsGenerationModalOpen(false)}
        onSelectOption={handleGenerationOptionSelected}
      />
    </>
  );
};
